import { HALVING_THRESHOLDS } from "./constants";

/**
 * Visual badge metadata for each EcoFutures edition.
 * Extracted from the Edition Badges (Map Context) design spec.
 */
export interface EditionBadge {
  /** Edition number (1-based) */
  edition: number;
  /** Tier label, e.g. "Edition 1" */
  tier: string;
  /** Gem / badge name */
  name: string;
  /** Dark base colour (hex) */
  base: string;
  /** Deep gem-specific flood colour for the active inner void (hex) */
  activeBg: string;
  /** Facet colour 1 — brightest crown highlight (hex) */
  f1: string;
  /** Facet colour 2 — crown mid-tone (hex) */
  f2: string;
  /** Facet colour 3 — pavilion mid-tone (hex) */
  f3: string;
  /** Facet colour 4 — darkest pavilion shadow (hex) */
  f4: string;
  /** Glow RGB string for radial-gradient, e.g. "188,146,248" */
  glow: string;
  /** Daily TR3 mint rate */
  rate: number;
  /** Glow pulse animation period in seconds */
  pulse: number;
  /** Whether the gem uses prismatic (rainbow) facet colours */
  prismatic: boolean;
  /** Embossed outer ring accent colour (rgba string) */
  ringCol: string;
  /** Cumulative halving threshold for this edition */
  threshold: number;
  /** Whether this edition is the currently active (minting) edition */
  active: boolean;
}

/**
 * All 12 edition badges ordered by edition number (index 0 = Edition 1).
 */
export const EDITION_BADGES: EditionBadge[] = [
  {
    edition: 1,
    tier: "Edition 1",
    name: "Rainbow Diamond",
    base: "#4C1D95",
    activeBg: "#2c0b59",
    f1: "#ffffff",
    f2: "#d4a5ff",
    f3: "#a855f7",
    f4: "#7e22ce",
    glow: "188,146,248",
    rate: 27.3791,
    pulse: 0.5,
    prismatic: true,
    ringCol: "rgba(188,146,248,0.35)",
    active: false,
    threshold: HALVING_THRESHOLDS[0],
  },
  {
    edition: 2,
    tier: "Edition 2",
    name: "Blue Diamond",
    base: "#1e3a8a",
    activeBg: "#0f2054",
    f1: "#bfdbfe",
    f2: "#3b82f6",
    f3: "#2563eb",
    f4: "#1d4ed8",
    glow: "31,163,255",
    rate: 27.3791,
    pulse: 0.6,
    prismatic: false,
    ringCol: "rgba(188,146,248,0.35)",
    active: false,
    threshold: HALVING_THRESHOLDS[1],
  },
  {
    edition: 3,
    tier: "Edition 3",
    name: "Red Diamond",
    base: "#7f1d1d",
    activeBg: "#4a0e0e",
    f1: "#fecaca",
    f2: "#ef4444",
    f3: "#dc2626",
    f4: "#991b1b",
    glow: "255,26,26",
    rate: 13.6895,
    pulse: 0.7,
    prismatic: false,
    ringCol: "rgba(188,146,248,0.35)",
    active: false,
    threshold: HALVING_THRESHOLDS[2],
  },
  {
    edition: 4,
    tier: "Edition 4",
    name: "Ruby",
    base: "#831843",
    activeBg: "#520d28",
    f1: "#fbcfe8",
    f2: "#ec4899",
    f3: "#db2777",
    f4: "#be185d",
    glow: "255,0,85",
    rate: 9.1263,
    pulse: 0.9,
    prismatic: false,
    ringCol: "rgba(188,146,248,0.35)",
    active: false,
    threshold: HALVING_THRESHOLDS[3],
  },
  {
    edition: 5,
    tier: "Edition 5",
    name: "Blue Sapphire",
    base: "#1e3a8a",
    activeBg: "#112359",
    f1: "#bfdbfe",
    f2: "#60a5fa",
    f3: "#2563eb",
    f4: "#1e40af",
    glow: "26,83,255",
    rate: 5.4758,
    pulse: 1.1,
    prismatic: false,
    ringCol: "rgba(188,146,248,0.35)",
    active: false,
    threshold: HALVING_THRESHOLDS[4],
  },
  {
    edition: 6,
    tier: "Edition 6",
    name: "Emerald",
    base: "#064e3b",
    activeBg: "#032b20",
    f1: "#a7f3d0",
    f2: "#10b981",
    f3: "#059669",
    f4: "#047857",
    glow: "0,230,115",
    rate: 3.4223,
    pulse: 1.3,
    prismatic: false,
    ringCol: "rgba(188,146,248,0.35)",
    active: false,
    threshold: HALVING_THRESHOLDS[5],
  },
  {
    edition: 7,
    tier: "Edition 7",
    name: "Jadeite",
    base: "#065f46",
    activeBg: "#043627",
    f1: "#a7f3d0",
    f2: "#34d399",
    f3: "#10b981",
    f4: "#059669",
    glow: "0,255,153",
    rate: 2.106,
    pulse: 1.6,
    prismatic: false,
    ringCol: "rgba(188,146,248,0.35)",
    active: false,
    threshold: HALVING_THRESHOLDS[6],
  },
  {
    edition: 8,
    tier: "Edition 8",
    name: "Paraíba Tourmaline",
    base: "#164e63",
    activeBg: "#0a2e3b",
    f1: "#a5f3fc",
    f2: "#06b6d4",
    f3: "#0891b2",
    f4: "#0e7490",
    glow: "0,238,255",
    rate: 1.3037,
    pulse: 2.0,
    prismatic: false,
    ringCol: "rgba(188,146,248,0.35)",
    active: false,
    threshold: HALVING_THRESHOLDS[7],
  },
  {
    edition: 9,
    tier: "Edition 9",
    name: "Alexandrite",
    base: "#581c87",
    activeBg: "#330e52",
    f1: "#e9d5ff",
    f2: "#a855f7",
    f3: "#9333ea",
    f4: "#7e22ce",
    glow: "204,51,255",
    rate: 0.8052,
    pulse: 2.4,
    prismatic: false,
    ringCol: "rgba(188,146,248,0.35)",
    active: false,
    threshold: HALVING_THRESHOLDS[8],
  },
  {
    edition: 10,
    tier: "Edition 10",
    name: "Padparadscha",
    base: "#7c2d12",
    activeBg: "#471708",
    f1: "#fed7aa",
    f2: "#f97316",
    f3: "#ea580c",
    f4: "#c2410c",
    glow: "255,102,0",
    rate: 0.4978,
    pulse: 2.8,
    prismatic: false,
    ringCol: "rgba(188,146,248,0.35)",
    active: false,
    threshold: HALVING_THRESHOLDS[9],
  },
  {
    edition: 11,
    tier: "Edition 11",
    name: "Fire Opal",
    base: "#78350f",
    activeBg: "#451c06",
    f1: "#fde68a",
    f2: "#f59e0b",
    f3: "#d97706",
    f4: "#b45309",
    glow: "255,153,0",
    rate: 0.3076,
    pulse: 3.2,
    prismatic: false,
    ringCol: "rgba(188,146,248,0.35)",
    active: false,
    threshold: HALVING_THRESHOLDS[10],
  },
  {
    edition: 12,
    tier: "Edition 12",
    name: "Gold Nugget",
    base: "#713f12",
    activeBg: "#402208",
    f1: "#fef08a",
    f2: "#eab308",
    f3: "#ca8a04",
    f4: "#a16207",
    glow: "255,204,0",
    rate: 0.1901,
    pulse: 3.5,
    prismatic: false,
    ringCol: "rgba(188,146,248,0.35)",
    active: false,
    threshold: HALVING_THRESHOLDS[11],
  },
];

/**
 * Get the badge data for a specific edition number.
 * @param edition — 1-based edition number (1–12)
 * @param active — whether this badge should be in its active (minting/glowing) state
 * @returns The EditionBadge with the active flag set, or undefined if out of range.
 */
export const getEditionBadge = (
  edition: number,
  active: boolean = false
): EditionBadge | undefined => {
  const badge = EDITION_BADGES[edition - 1];
  if (!badge) return undefined;
  return { ...badge, active };
};
